import type { SxProps, Theme } from '@mui/material/styles'
import { poolwerxColors } from '../../theme/colors'

/**
 * Shared button styling for the standalone auth / utility cards (Login,
 * Page Unavailable, Access Denied, Choose Franchise). Consolidates the
 * near-identical inline `sx` blocks those screens used to duplicate.
 *
 * Single hover rule: every filled-teal button hovers to one shade -
 * `poolwerxColors.tealHover` (#006680). No two filled-teal buttons may
 * hover to different shades.
 */

/**
 * Note: these are typed with `satisfies SxProps<Theme>` (not an annotation) so
 * each keeps its narrow object-literal type. That lets callers either pass one
 * directly (`sx={primaryTealButtonSx}`) or compose it in MUI's array form
 * (`sx={[secondaryTealButtonSx, { ...overrides }]}`) - a plain `SxProps<Theme>`
 * annotation widens the type and breaks the array-element case.
 */

/** 2px teal focus-visible ring, standardised across all auth-card controls. */
export const focusRingSx = {
  '&:focus-visible': {
    outline: `2px solid ${poolwerxColors.teal}`,
    outlineOffset: '2px',
  },
} satisfies SxProps<Theme>

/** Filled teal primary action (e.g. "Go to Login", "Choose Franchise"). */
export const primaryTealButtonSx = {
  backgroundColor: poolwerxColors.teal,
  color: '#fff',
  fontWeight: 600,
  fontSize: '16px',
  minHeight: 52,
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': { backgroundColor: poolwerxColors.tealHover },
  ...focusRingSx,
} satisfies SxProps<Theme>

/** Outlined teal secondary action (e.g. "Go Back", "Admin Panel"). */
export const secondaryTealButtonSx = {
  borderColor: poolwerxColors.teal,
  color: poolwerxColors.teal,
  fontWeight: 600,
  fontSize: '16px',
  minHeight: 52,
  borderRadius: '8px',
  textTransform: 'none',
  '&:hover': {
    borderColor: poolwerxColors.tealHover,
    backgroundColor: 'rgba(0, 127, 152, 0.04)',
  },
  ...focusRingSx,
} satisfies SxProps<Theme>

/**
 * "Sign in with Google" CTA on the Login card. Deliberately light (not a heavy
 * filled block): white, Google-brand-compliant - white bg, hairline border,
 * charcoal text, full-colour G, no shadow.
 */
export const googleSignInButtonSx = {
  backgroundColor: '#fff',
  color: poolwerxColors.charcoal,
  border: '1px solid #dadce0',
  fontWeight: 600,
  fontSize: '15px',
  minHeight: 46,
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: 'none',
  '& .MuiButton-startIcon': { marginRight: '12px' },
  '&:hover': {
    backgroundColor: '#f8fafb',
    border: '1px solid #dadce0',
    boxShadow: 'none',
  },
  '&:active': {
    transform: 'translateY(1px)',
  },
  '&:disabled': {
    backgroundColor: '#fff',
    border: '1px solid #ebedf0',
    color: '#9aa0a6',
  },
  ...focusRingSx,
} satisfies SxProps<Theme>
